import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-20 px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">分类未找到</h1>
            <p className="text-foreground-500 mb-6">请检查您访问的分类地址是否正确</p>
            <Link to="/" className="text-primary-500 font-medium hover:text-primary-600">返回首页</Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - http 91.com 官方指南`,
    description: category.description,
    url: `https://www.91.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'http 91.com 官方指南',
      url: 'https://www.91.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
            <p className="text-xs text-foreground-400">共 {articles.length} 篇文章</p>
          </div>
        </section>

        {/* Articles List */}
        <section className="w-full px-4 md:px-6 py-12">
          <div className="max-w-4xl mx-auto">
            {articles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-background-50 border border-background-200/60 rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-primary-200 group cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">{article.summary}</p>
                        <div className="flex items-center flex-wrap gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime} 分钟阅读
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.publishDate}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {article.author}
                          </span>
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-background-100 text-foreground-300 group-hover:bg-primary-100 group-hover:text-primary-500 transition-colors flex-shrink-0">
                        <i className="ri-arrow-right-line text-lg"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="w-full px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-5">其他分类</h2>
            <div className="flex flex-wrap gap-3">
              {categories
                .filter((c) => c.slug !== category.slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-lg text-sm text-foreground-700 hover:border-accent-200 hover:text-accent-600 transition-colors whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}